<template>
  <div>
    <TopBar currentTitle="Recipes">
      <b-button type="is-primary" size="is-small" icon-left="plus" @click="createNewRecipe">Add Recipe</b-button>
    </TopBar>
    <div class="page-content">
    <!-- Page Info -->
    <h1 class="page-title">Recipes</h1>
    <p class="page-subtitle mb-4">
      <template v-if="hasActiveFilter">{{ filteredRecipes.length }} of {{ recipes.length }} recipes</template>
      <template v-else>{{ recipes.length }} total</template>
      ({{ this.freeRatio }}% free)
    </p>

    <!-- Filters Bar -->
    <div class="filters-bar">
      <div class="search-wrapper">
        <b-icon icon="magnify" class="search-icon" />
        <b-input
          placeholder="Search recipes..."
          v-model="query"
          @input="performSearch"
          expanded>
        </b-input>
      </div>
      <div class="filters-right">
        <a v-if="hasActiveFilter || query" class="clear-filters" @click="clearAll">Clear all</a>
        <b-select v-model="statusFilter" size="is-small" @input="onStatusFilterChange" :class="{ 'filter-active': statusFilter }">
          <option :value="''">All statuses</option>
          <option value="draft">Draft</option>
          <option value="test">Test</option>
          <option value="fix">Fix</option>
          <option value="live">Live</option>
        </b-select>
        <b-select v-model="tagsFilter" size="is-small" @input="onTagsFilterChange" :class="{ 'filter-active': tagsFilter }">
          <option :value="''">All tags</option>
          <option value="has_tags">Has tags</option>
          <option value="no_tags">No tags</option>
        </b-select>
        <b-select v-model="variantFilter" size="is-small" @input="onVariantFilterChange" :class="{ 'filter-active': variantFilter }">
          <option :value="''">All recipes</option>
          <option value="variants_only">Variants only</option>
          <option value="originals_only">Base only</option>
        </b-select>
      </div>
    </div>

    <!-- Table Card -->
    <div class="feeds-card">
      <div class="feeds-card-body compact">
        <b-table
          :data="filteredRecipes"
          :hoverable="true"
          :mobile-cards="false"
          :paginated="true"
          :per-page="perPage"
          :current-page.sync="currentPage"
          :default-sort="[sortField, sortDirection]"
          @sort="onSort"
          pagination-position="bottom"
          @click="openRecipe">

          <b-table-column width=48 label="Image" sortable v-slot="props">
            <figure class="m-0 p-0 image is-48x48">
              <img v-if="rowImageSrc(props.row) == null" class="thumbnail" src="@/assets/listing-placeholder.png">
              <img v-else class="thumbnail" :src="rowImageSrc(props.row)">
            </figure>
          </b-table-column>

          <b-table-column field="title" label="Title" sortable v-slot="props">
            <p class="has-text-weight-bold">
              {{ rowTitle(props.row) }}
            </p>
          </b-table-column>

          <b-table-column v-if="isDesktop()" field="status" width=100 label="Status" sortable centered v-slot="props">
            <FeedsStatusBadge :status="props.row.status" />
          </b-table-column>

          <b-table-column v-if="isDesktop()" field="is_free" width=50 label="Free" sortable centered v-slot="props">
            <b-tag
              size="is-medium"
              class="mr-1"
              :type="props.row.is_free ? 'is-success' : 'is-default'">
              <b-icon
                size="is-small"
                :type="props.row.is_free ? 'is-white' : 'is-default'"
                icon="star">
              </b-icon>
            </b-tag>
          </b-table-column>

          <b-table-column v-if="isDesktop()" field="servings" label="Serves" sortable v-slot="props">
            <p class="has-text-weight-bold has-text-centered">
              {{ props.row.servings }}
            </p>
          </b-table-column>

          <b-table-column v-if="isDesktop()" field="variant" label="Variant" sortable v-slot="props">
            <span v-if="props.row.is_variant" class="tag is-light">Variant</span>
          </b-table-column>

          <b-table-column v-if="isDesktop()" field="published_at" width=140 label="Published" sortable centered v-slot="props">
            <template v-if="props.row.published_at">
              <span class="date-tag">
                {{ new Date(props.row.published_at).toLocaleDateString() }}
              </span>
              <span v-if="isNew(props.row.published_at)" class="new-badge">NEW</span>
            </template>
            <span v-else class="date-tag">—</span>
          </b-table-column>

          <b-table-column field="tags" width=80 label="Tags" centered v-slot="props">
            <div
              v-if="props.row.tags && props.row.tags.length > 0"
              class="tags-preview"
              @mouseenter="showTagsPanel($event, props.row)"
              @mouseleave="hideTagsPanel">
              <b-icon icon="tag-multiple" size="is-small" />
              <span class="tags-count">{{ props.row.tags.length }}</span>
            </div>
            <span v-else class="date-tag">—</span>
          </b-table-column>

          <b-table-column field="options" custom-key="actions" width=20 v-slot="props">
            <b-dropdown aria-role="list" position="is-bottom-left" @click.native.stop>
              <template #trigger>
                <b-button icon-right="dots-vertical" />
              </template>
              <b-dropdown-item :disabled="props.row.is_variant" aria-role="listitem" @click="duplicateRecipe(props.row)">
                <b-icon size="is-small" icon="content-copy"></b-icon>
                {{ props.row.is_variant ? "Duplicating unavailable":"Duplicate" }}
              </b-dropdown-item>
              <b-dropdown-item class="has-text-danger" aria-role="listitem" @click="deleteRecipe(props.row)">
                <b-icon size="is-small" icon="delete"></b-icon>
                Delete
              </b-dropdown-item>
            </b-dropdown>
          </b-table-column>

          <template #empty>
            <div class="has-text-centered py-5">
              <p class="has-text-grey">No recipes found</p>
            </div>
          </template>
        </b-table>
        <div class="pagination-footer">
          <div class="page-size-selector">
            <span class="page-size-label">Show:</span>
            <b-select v-model="perPage" size="is-small" @input="savePerPage">
              <option :value="10">10</option>
              <option :value="25">25</option>
              <option :value="50">50</option>
              <option :value="100">100</option>
            </b-select>
          </div>
        </div>
      </div>
    </div>
    </div>

    <!-- Tags hover panel -->
    <div
      v-if="hoveredRecipeTags"
      class="tags-hover-panel"
      :style="tagsPanelStyle"
      @mouseenter="keepTagsPanel"
      @mouseleave="hideTagsPanel">
      <div class="tags-hover-header">Tags</div>
      <div class="tags-hover-list">
        <span v-for="tag in hoveredRecipeTags" :key="tag" class="tag is-light tags-hover-tag">{{ tag }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import db from "@/firebase/db"
import debounce from 'lodash/debounce'
import search from '../search'
import FeedsStatusBadge from '@/components/common/FeedsStatusBadge.vue'
import TopBar from '@/components/layout/TopBar.vue'

const STORAGE_KEY = 'feeds_admin_recipe_list_per_page'
const PAGE_STATE_KEY = 'feeds_list_page_RecipeListView'
const SCROLL_STATE_KEY = 'feeds_list_state_RecipeListView'
const SORT_STATE_KEY = 'feeds_list_sort_RecipeListView'
const FILTER_STATE_KEY = 'feeds_list_filter_RecipeListView'
const SEARCH_STATE_KEY = 'feeds_list_search_RecipeListView'

export default {
  name: 'RecipeListView',
  components: {
    FeedsStatusBadge,
    TopBar
  },
  data() {
    const savedSort = JSON.parse(sessionStorage.getItem(SORT_STATE_KEY) || 'null')
    return {
      recipes: [],
      query: sessionStorage.getItem(SEARCH_STATE_KEY) || "",
      perPage: parseInt(localStorage.getItem(STORAGE_KEY)) || 25,
      currentPage: parseInt(sessionStorage.getItem(PAGE_STATE_KEY)) || 1,
      sortField: savedSort?.field || 'created_at',
      sortDirection: savedSort?.direction || 'desc',
      statusFilter: sessionStorage.getItem(FILTER_STATE_KEY) || '',
      tagsFilter: sessionStorage.getItem(FILTER_STATE_KEY + '_tags') || '',
      variantFilter: sessionStorage.getItem(FILTER_STATE_KEY + '_variant') || '',
      scrollRestored: false,
      hoveredRecipeTags: null,
      tagsPanelStyle: {},
      tagsHoverTimeout: null
    }
  },
  watch: {
    currentPage(newPage) {
      sessionStorage.setItem(PAGE_STATE_KEY, newPage)
    },
    recipes() {
      // Restore scroll position after data loads
      if (!this.scrollRestored) {
        this.scrollRestored = true
        this.$nextTick(() => {
          const savedState = sessionStorage.getItem(SCROLL_STATE_KEY)
          if (savedState) {
            const state = JSON.parse(savedState)
            if (state.scrollY > 0) {
              setTimeout(() => {
                window.scrollTo(state.scrollX, state.scrollY)
              }, 50)
            }
          }
        })
      }
    }
  },
  mounted() {
    if (this.query) {
      this.performSearch(this.query)
    }
  },
  firestore: {
    recipes: db.collection('recipes').orderBy("created_at", "desc")
  },
  computed: {
    filteredRecipes() {
      let results = this.recipes
      if (this.statusFilter) {
        results = results.filter(r => r.status === this.statusFilter)
      }
      if (this.tagsFilter === 'has_tags') {
        results = results.filter(r => r.tags && r.tags.length > 0)
      } else if (this.tagsFilter === 'no_tags') {
        results = results.filter(r => !r.tags || r.tags.length === 0)
      }
      if (this.variantFilter === 'variants_only') {
        results = results.filter(r => r.is_variant)
      } else if (this.variantFilter === 'originals_only') {
        results = results.filter(r => !r.is_variant)
      }
      return results
    },
    hasActiveFilter() {
      return !!this.statusFilter || this.tagsFilter !== '' || this.variantFilter !== ''
    },
    freeRatio() {
      let total = this.recipes.length;
      let free = this.recipes.filter(recipe => recipe.is_free).length;
      return Math.round(free / total * 100);
    }
  },
  methods: {
    savePerPage(value) {
      localStorage.setItem(STORAGE_KEY, value)
    },
    onSort(field, direction) {
      this.sortField = field
      this.sortDirection = direction
      sessionStorage.setItem(SORT_STATE_KEY, JSON.stringify({ field, direction }))
    },
    onStatusFilterChange(value) {
      sessionStorage.setItem(FILTER_STATE_KEY, value)
      this.currentPage = 1
    },
    onTagsFilterChange(value) {
      sessionStorage.setItem(FILTER_STATE_KEY + '_tags', value)
      this.currentPage = 1
    },
    onVariantFilterChange(value) {
      sessionStorage.setItem(FILTER_STATE_KEY + '_variant', value)
      this.currentPage = 1
    },
    clearAll() {
      this.statusFilter = ''
      this.tagsFilter = ''
      this.variantFilter = ''
      this.query = ''
      sessionStorage.removeItem(FILTER_STATE_KEY)
      sessionStorage.removeItem(FILTER_STATE_KEY + '_tags')
      sessionStorage.removeItem(FILTER_STATE_KEY + '_variant')
      sessionStorage.removeItem(SEARCH_STATE_KEY)
      this.currentPage = 1
      this.performSearch('')
    },
    performSearch: debounce(function (query) {
        if (this.query !== query) {
            this.query = query;
            this.recipes = [];
        }
        sessionStorage.setItem(SEARCH_STATE_KEY, query)
        if (!query.length) {
            db.collection("recipes").orderBy("created_at", "desc").get().then(snapshot => {
              this.recipes = snapshot.docs.map(docSnapshot => {
                let doc = docSnapshot.data();
                doc.id = docSnapshot.id;
                return doc;
              });
            });
            return;
        }

        let searchParameters = {
          'q'         : query,
          'preset'    : 'admin_recipe_search',
          'sort_by': '_text_match:desc',
          'per_page': '100'
        };

        let that = this;
        search.collections('recipes')
          .documents()
          .search(searchParameters)
          .then(function (searchResults) {
            that.recipes = searchResults.hits.map((hit) => hit.document );
          })
          .finally(() => {
            that.isLoading = false;
          })
      }, 500),
    isDesktop() {
      if (screen.width <= 760) {
        return false;
      } else {
        return true;
      }
    },
    createNewRecipe() {
      db.collection("recipes").add({
          created_at: Date.now(),
          title: "New Recipe",
          steps: [],
          ingredients: [],
          tips: [],
          servings: 2,
          image: {},
          images: [],
          status: "draft",
          is_variant: false,
          is_free: true,
          tags: [],
          search_synonyms: [],
          seasonal_availability: { year_round: true, months: [] }
      })
      .then((docRef) => {
          console.log("Document written with ID: ", docRef.id);
          this.$router.push({ name: 'RecipeView', params: { id: docRef.id } })
      })
      .catch((error) => {
          console.error("Error adding document: ", error);
      });
    },
    openRecipe(recipe) {
      this.$router.push({ name: 'RecipeView', params: { id: recipe.id } })
    },
    deleteRecipe(recipe) {
        this.$buefy.dialog.confirm({
            title: 'Deleting recipe',
            message: `Are you sure you want to delete recipe: <b><i>"${recipe.title}"</i></b>? <br /><br />This action cannot be undone.`,
            confirmText: 'Delete Recipe',
            type: 'is-danger',
            hasIcon: true,
            onConfirm: () => {
              db.collection("recipes").doc(recipe.id)
              .delete()
              .then(() => {
                this.$buefy.toast.open({type: 'is-success', message: `Recipe successfully deleted!`})
                this.performSearch(this.query);
              }).catch((error) => {
                this.$buefy.toast.open({type: 'is-danger', message: `Error deleting recipe: ${error}`})
              });
            }
        })
    },
    duplicateRecipe(recipe) {
      if (recipe.is_variant){
        this.$buefy.toast.open({type: 'is-danger', message: `You can't duplicate a variant!`});
        return;
      }
      db.collection("recipes").doc(recipe.id)
        .get()
        .then((doc) => {
          if (doc.exists && doc.data()) {
            let newData = doc.data();
            newData['created_at'] = Date.now();
            newData['status'] = "draft";
            newData['is_variant'] = true;
            newData['variants'] = null;
            newData['name'] = `Copy`;

            let baseId = recipe.is_variant ? recipe.base_recipe.id : recipe.id;
            let baseTitle = recipe.is_variant ? recipe.base_recipe.title : recipe.title;
            newData['base_recipe'] = {
              'id': baseId,
              'title': baseTitle
            };

            return db.collection("recipes").add(newData)
              .then((docRef) => {
                this.addVariantToBase(docRef.id, recipe);
                this.$buefy.toast.open({type: 'is-success', message: `Recipe successfully duplicated! ${docRef.id}`});
                this.performSearch(this.query);
              });
          }
        })
        .catch((error) => {
          console.log(`Error duplicating base recipe: ${error}`);
          this.$buefy.toast.open({type: 'is-danger', message: `Error duplicating recipe ${recipe.id}: ${error}`})
        });
    },
    addVariantToBase (variant, baseRecipe) {
      if (baseRecipe.recipe_variants == null){
        baseRecipe.recipe_variants = {};
      }
      baseRecipe.recipe_variants[variant] = {'id': variant};

      console.log(`Merged: ${baseRecipe.recipe_variants}`);
      return db.collection('recipes')
        .doc(baseRecipe.id)
        .set(baseRecipe)
        .then(() => {
          this.$buefy.toast.open({type: 'is-success', message: `Base recipe successfully updated!`});
        })
        .catch((error) => {
          console.log(`Error saving base recipe: ${error}`);
          this.$buefy.toast.open({type: 'is-danger', message: `Error saving base recipe: ${error}`});
        });
    },
    showTagsPanel(event, recipe) {
      clearTimeout(this.tagsHoverTimeout);
      const rect = event.currentTarget.getBoundingClientRect();
      this.tagsPanelStyle = {
        top: `${rect.bottom + window.scrollY + 4}px`,
        left: `${rect.left + window.scrollX}px`
      };
      this.hoveredRecipeTags = recipe.tags;
    },
    keepTagsPanel() {
      clearTimeout(this.tagsHoverTimeout);
    },
    hideTagsPanel() {
      this.tagsHoverTimeout = setTimeout(() => {
        this.hoveredRecipeTags = null;
      }, 150);
    },
    isNew(publishedAt) {
      const sixWeeksMs = 6 * 7 * 24 * 60 * 60 * 1000;
      return Date.now() - publishedAt < sixWeeksMs;
    },
    rowImageSrc(recipe) {
      if (recipe.images == null){
        return null;
      }
      return (recipe.images.length != 0) ? recipe.images[0].src : null;
    },
    rowTitle(recipe) {
      if (recipe.name != null && recipe.name != ""){
        return `${recipe.title} (${recipe.name})`
      }
      return recipe.title
    }
  }
}
</script>

<style scoped>
.search-wrapper {
  flex: 1;
  max-width: 400px;
  position: relative;
}

.search-wrapper .search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1;
  color: var(--text-muted);
}

.search-wrapper .input {
  padding-left: 40px !important;
}

.thumbnail {
  height: 100% !important;
  object-fit: cover;
  border-radius: var(--radius-sm);
}

.date-tag {
  font-size: 12px;
  color: var(--text-secondary);
}

.tags-preview {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  cursor: default;
  color: var(--text-secondary);
}

.tags-count {
  font-size: 12px;
  font-weight: 600;
}

.tags-hover-panel {
  position: absolute;
  z-index: 100;
  background: white;
  border-radius: var(--radius-md);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12), 0 1px 4px rgba(0, 0, 0, 0.08);
  padding: 12px;
  min-width: 180px;
  max-width: 300px;
}

.tags-hover-header {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--text-muted);
  margin-bottom: 8px;
}

.tags-hover-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.tags-hover-tag {
  font-size: 12px;
}

.new-badge {
  display: inline-block;
  margin-left: 6px;
  padding: 1px 6px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.5px;
  color: white;
  background-color: var(--feeds-green, #00857a);
  border-radius: 8px;
  vertical-align: middle;
}
</style>
